<template>
  <v-container px-0
    py-0><!-- 'white-menu-theme' class needs bound on here as per MainMenu for theme to work on Case menu -->
    <Loading :isLoading="isLoading">
      <div v-bar class="v-bar-wrapper">
        <v-layout xs12 wrap fluid row style="position: relative; width: 100%" class="aside-menus">
          <v-list class="pt-0 pa-0" dense>
            <h4 class="text-truncate opacity-54 pt-3 pl-3 pb-2">{{ caseTypeName }} - {{ caseTitle }}</h4>
            <LeafNode v-for="item in caseMenu" :key="item.title" :item="item" :parentMenuLabel="parentMenuLabel"
              class="indented"></LeafNode>
          </v-list>
          <v-list class="pt-0" dense>
            <LeafNode v-for="item in relatedCasesMenu" :key="item.title" :item="item" :parentMenuLabel="parentMenuLabel"
              class="indented"></LeafNode>
            <!-- Recent Cases Menu -->
            <v-divider></v-divider>

            <h4 class="text-truncate opacity-54 pt-3 pl-3 pb-2">RECENT CASES</h4>
            <LeafNode v-for="(recentCase, index) in lastFiveCases" :key="index" :item="menuItem(recentCase)"
              :parentMenuLabel="parentMenuLabel" class="indented"></LeafNode>

            <v-divider class="mt-0 mb-0"></v-divider>
          </v-list>
          <!-- Back button -->
          <v-layout row xs12 class="menu-back-link pt-3 pb-2" @click="backToMainMenu">
            <font-awesome-icon class="menu-back-link--icon" :icon="['fas', 'chevron-left']"></font-awesome-icon>
            <h4 class="pl-1 pr-3 menu-back-link--text text-truncate">Back to {{ lastCaseContainerTitle }}</h4>
          </v-layout>
        </v-layout>
      </div>
    </Loading>
  </v-container>
</template>

<script>
import Loading from "@/components/shared/Loading.vue"
import Logo from "@/components/Logo.vue"
import LeafNode from "@/components/navigation/LeafNode"
import { mapState } from "vuex"
import Helpers from "@/helpers.js"

export default {
  name: "InCaseMenu",
  components: { Loading, Logo, LeafNode },
  props: { referrer: String },
  data: () => ({
    drawer: true,
    mini: false,
    right: null,
  }),
  computed: {
    ...mapState({
      recentCases: (state) => state.homepage.recent_cases,
      isAdminMode: (state) => state.currentUser.isAccountAdmin,
      lastCaseContainerPath: (state) => state.ui.lastCaseContainerPath,
      currentCase: (state) => state.cases.currentlySelected.case,
      notesCount: (state) => (state.cases.currentlySelected.case.notes ? state.cases.currentlySelected.case.notes.length : 0),
      fileAttsCount: (state) => (state.cases.currentlySelected.case.fileAttachments ? state.cases.currentlySelected.case.fileAttachments.filter((f) => !f.hidden).length : 0),
      conversationsCount: (state) => {
        const currentCase = state.cases.currentlySelected.case
        if (!currentCase) return 0
        if (typeof currentCase.conversationCount === "number") return currentCase.conversationCount
        return currentCase.conversations ? currentCase.conversations.length : 0
      },
      unreadConversationsCount: (state) => state.cases.currentlySelected.case.noUnreadConversations,
      caseTypeCode: (state) => (state.caseTypes && state.caseTypes.current ? state.caseTypes.current.code : ""),
      caseTypeName: (state) => (state.caseTypes && state.caseTypes.current ? state.caseTypes.current.name : ""),
      caseLoading: (state) => state.cases.currentlySelected.loading,
      currentUser: (state) => state.currentUser,
      userLoading: (state) => state.currentUser.isCurrentUserLoading,
      menuState: (state) => state.ui.menu,
      menuColor: (state) => state.ui.menuColor,
      permissions: (state) => state.ui.grantedPermissions,
      caseId: (state) => state.cbroute.params.case_id,
      relatedCasesMenu: (state) => {
        const related = []
        const caseId = state.cbroute.params.case_id
        const currentCase = state.cases.currentlySelected.case
        const caseTypes = state.caseTypes && state.caseTypes.all ? state.caseTypes.all : []
        if (currentCase && caseTypes && caseTypes.length > 0) {
          const fromCaseType = caseTypes.find((ct) => ct.id === currentCase.caseTypeId)
          if (fromCaseType && fromCaseType.relationships) {
            const relationships = fromCaseType.relationships
            relationships.forEach(function (relation) {
              const relatedType = caseTypes.find((currentType) => currentType.id === relation.toId)
              if (relatedType) {
                const relatedName = Helpers.pluralize(Helpers.caseTypeName(relatedType))
                const duplicateType = related.find((currentType) => currentType.title === relatedName)
                if (!duplicateType) {
                  related.push({
                    title: relatedName,
                    route: `/case/${caseId}/related-${relatedType.code}`,
                    iconCode: relatedType.iconCode,
                    iconSet: relatedType.iconSet,
                  })
                }
              }
            })
          }
        }
        return related
      },
    }),
    parentMenuLabel() {
      return this.currentCase && this.currentCase.title ? this.currentCase.title : ""
    },
    lastCaseContainerTitle() {
      if (this.lastCaseContainerPath && this.lastCaseContainerPath.length > 0) {
        const title = this.lastCaseContainerPath[this.lastCaseContainerPath.length - 1].pageTitle
        return title === "Caseblocks" ? "Dashboard Overview" : title
      } else {
        return "Dashboard Overview"
      }
    },
    lastFiveCases() {
      const recentCasesWithoutCurrentCase = this.recentCases.filter((rc) => rc.id !== this.caseId)
      if (recentCasesWithoutCurrentCase && recentCasesWithoutCurrentCase.length > 0) {
        const sliceEnd = recentCasesWithoutCurrentCase.length > 5 ? 5 : recentCasesWithoutCurrentCase.length
        return recentCasesWithoutCurrentCase.slice(0, sliceEnd)
      }
      return []
    },
    menuToggle: {
      get() {
        return this.menuState !== "main" && this.menuState !== "case"
      },
      set() {
        this.toggleMenu()
      },
    },
    isLoading() {
      return this.caseLoading && this.userLoading
    },
    caseTitle() {
      if (this.currentCase && this.currentCase.title) {
        return this.currentCase.title
      } else {
        return ""
      }
    },
    caseMenu() {
      const menu = [
        {
          title: "Case Summary",
          iconCode: "file-search",
          iconSet: "far",
          route: `/case/${this.caseId}/details`,
        },
        {
          title: "Timeline",
          iconCode: "chart-line",
          iconSet: "far",
          route: `/case/${this.caseId}/timeline`,
        },
      ]
      const notes = {
        title: `Notes ${this.formatCount(this.notesCount)}`,
        iconCode: "pencil",
        iconSet: "far",
        section: "notes",
        route: `/case/${this.caseId}/notes`,
      }
      const conversations = {
        title: `Conversations ${this.formatCount(this.conversationsCount, this.unreadConversationsCount)}`,
        iconCode: "comments",
        iconSet: "far",
        section: "conversations",
        route: `/case/${this.caseId}/conversations`,
      }
      const documents = {
        // title: `Documents ${this.formatCount(this.fileAttsCount)}`,
        title: 'Documents',
        iconCode: "file",
        iconSet: "far",
        section: "documents",
        route: `/case/${this.caseId}/documents`,
      }
      const tasks = {
        title: "Tasks",
        iconCode: "check",
        iconSet: "far",
        route: `/case/${this.caseId}/tasks`,
      }

      if (this.hasPermission("Case Notes")) {
        menu.push(notes)
      }
      if (this.hasPermission("Conversations")) {
        menu.push(conversations)
      }
      if (this.hasPermission("Documents")) {
        menu.push(documents)
      }
      menu.push(tasks)
      return menu
    },
  },
  menuToggle() {
    // TODO - Reroute to appropriate counterpart
    if (this.menuToggle) {
      console.log("Menu Toggled [CONFIG], route: ", this.$route)
    } else {
      console.log("Menu Toggled [CASEWORK], route: ", this.$route)
    }
  },
  methods: {
    formatCount(total, subtotal) {
      if (subtotal) {
        return total ? `(${subtotal} / ${total})` : ""
      } else {
        return total ? `(${total})` : ""
      }
    },
    menuItem(recentCase) {
      return {
        title: recentCase.title,
        iconSet: recentCase.icon.set,
        iconCode: recentCase.icon.code,
        route: { name: "Case", params: { case_id: recentCase.id } },
      }
    },
    toggleMenu() {
      this.$store.dispatch("switchMenu", { opposite: true })
    },
    backToMainMenu() {
      if (this.lastCaseContainerPath && this.lastCaseContainerPath.length > 0) {
        const container = this.lastCaseContainerPath.pop()
        if (container.path.includes("/case/") && container.path.includes("/new")) {
          this.backToMainMenu() // Skip New Case page
        } else {
          this.$router.push(container.path)
        }
      } else {
        this.$router.push({ name: "Dashboard" })
      }
    },
    hasPermission(permission) {
      if (this.isAdminMode) {
        return true
      }
      return Helpers.hasPermission(this.permissions, permission, this.caseTypeCode)
    },
  },
}
</script>

<style>
.casework {
  min-height: 100vh;
}

.configuration {
  background-color: #1d303e;
  min-height: 100vh;
}
</style>

<style lang="scss" scoped>
.menu-back-link {
  align-items: center;
  cursor: pointer;
  padding-left: 12px;
}
.menu-back-link--icon {
  flex: 0 0 auto;
  width: 1em;
  margin-right: 8px;
}
</style>
